/**
 * Menu Items Configuration
 * Definisce la mappatura tra menu items e routes per ogni company type
 */

export interface MenuItemConfig {
    key: string;
    routes: string[];
    translationKey: string;
    icon?: string;
}

export type CompanyTypeKey = 'shipper' | 'receiver' | 'freight_forwarder' | 'sender' | 'carrier' | 'port_authority' | 'customs_broker' | 'insurance_provider';

/**
 * Configurazione completa dei menu items per ogni company type
 */
export const MENU_ITEMS_BY_TYPE: Record<CompanyTypeKey, Record<string, MenuItemConfig>> = {
    // SHIPPER/SENDER - Same configuration
    shipper: {
        'ask-eve': {
            key: 'ask-eve',
            routes: ['/shipper/ask-eve'],
            translationKey: 'askEve'
        },
        controlHub: {
            key: 'controlHub',
            routes: ['/shipper/control-hub'],
            translationKey: 'controlHub'
        },
        containers: {
            key: 'containers',
            routes: [
                '/shipper/containers',
                '/shipper/containers/new',
                '/shipper/containers/triage',
                '/shipper/containers/document-triage',
                '/shipper/containers/upload',
                '/shipper/containers/document-review',
                '/shipper/containers/document-history'
            ],
            translationKey: 'containers'
        },
        quotes: {
            key: 'quotes',
            routes: [
                '/shipper/quotes',
                '/shipper/quotes/settings'
            ],
            translationKey: 'quotes'
        },
        documents: {
            key: 'documents',
            routes: ['/shipper/documents'],
            translationKey: 'eveDrive'
        },
        liveTracking: {
            key: 'liveTracking',
            routes: ['/shipper/live-tracking'],
            translationKey: 'liveTracking'
        },
        businessPartners: {
            key: 'businessPartners',
            routes: ['/shipper/business-partners'],
            translationKey: 'businessPartners'
        },
        orders: {
            key: 'orders',
            routes: ['/shipper/orders'],
            translationKey: 'orders'
        },
        activity: {
            key: 'activity',
            routes: ['/shipper/activity'],
            translationKey: 'activity'
        },
        issues: {
            key: 'issues',
            routes: ['/shipper/issues'],
            translationKey: 'issues'
        },
        reviewCenter: {
            key: 'reviewCenter',
            routes: ['/shipper/review-center'],
            translationKey: 'reviewCenter'
        },
        team: {
            key: 'team',
            routes: ['/shipper/team'],
            translationKey: 'team'
        },
        addressBook: {
            key: 'addressBook',
            routes: ['/shipper/address-book'],
            translationKey: 'addressBook'
        },
        settings: {
            key: 'settings',
            routes: ['/shipper/profile'],
            translationKey: 'settings'
        }
    },

    // SENDER - Alias per SHIPPER
    sender: {
        'ask-eve': {
            key: 'ask-eve',
            routes: ['/shipper/ask-eve'],
            translationKey: 'askEve'
        },
        controlHub: {
            key: 'controlHub',
            routes: ['/shipper/control-hub'],
            translationKey: 'controlHub'
        },
        containers: {
            key: 'containers',
            routes: [
                '/shipper/containers',
                '/shipper/containers/new',
                '/shipper/containers/triage',
                '/shipper/containers/document-triage',
                '/shipper/containers/upload',
                '/shipper/containers/document-review',
                '/shipper/containers/document-history'
            ],
            translationKey: 'containers'
        },
        quotes: {
            key: 'quotes',
            routes: [
                '/shipper/quotes',
                '/shipper/quotes/settings'
            ],
            translationKey: 'quotes'
        },
        documents: {
            key: 'documents',
            routes: ['/shipper/documents'],
            translationKey: 'eveDrive'
        },
        liveTracking: {
            key: 'liveTracking',
            routes: ['/shipper/live-tracking'],
            translationKey: 'liveTracking'
        },
        businessPartners: {
            key: 'businessPartners',
            routes: ['/shipper/business-partners'],
            translationKey: 'businessPartners'
        },
        orders: {
            key: 'orders',
            routes: ['/shipper/orders'],
            translationKey: 'orders'
        },
        activity: {
            key: 'activity',
            routes: ['/shipper/activity'],
            translationKey: 'activity'
        },
        issues: {
            key: 'issues',
            routes: ['/shipper/issues'],
            translationKey: 'issues'
        },
        reviewCenter: {
            key: 'reviewCenter',
            routes: ['/shipper/review-center'],
            translationKey: 'reviewCenter'
        },
        team: {
            key: 'team',
            routes: ['/shipper/team'],
            translationKey: 'team'
        },
        addressBook: {
            key: 'addressBook',
            routes: ['/shipper/address-book'],
            translationKey: 'addressBook'
        },
        settings: {
            key: 'settings',
            routes: ['/shipper/profile'],
            translationKey: 'settings'
        }
    },

    // RECEIVER
    receiver: {
        'ask-eve': {
            key: 'ask-eve',
            routes: ['/receiver/ask-eve'],
            translationKey: 'askEve'
        },
        controlHub: {
            key: 'controlHub',
            routes: ['/receiver/control-hub'],
            translationKey: 'controlHub'
        },
        calendar: {
            key: 'calendar',
            routes: ['/receiver/calendar'],
            translationKey: 'calendar'
        },
        shippers: {
            key: 'shippers',
            routes: ['/receiver/shippers'],
            translationKey: 'shippers'
        },
        containers: {
            key: 'containers',
            routes: [
                '/receiver/containers',
                '/receiver/containers/document-review',
                '/receiver/containers/document-history'
            ],
            translationKey: 'containers'
        },
        liveTracking: {
            key: 'liveTracking',
            routes: ['/receiver/live-tracking'],
            translationKey: 'liveTracking'
        },
        orders: {
            key: 'orders',
            routes: [
                '/receiver/orders',
                '/receiver/orders/new'
            ],
            translationKey: 'orders'
        },
        activity: {
            key: 'activity',
            routes: ['/receiver/activity'],
            translationKey: 'activity'
        },
        team: {
            key: 'team',
            routes: ['/receiver/team'],
            translationKey: 'team'
        },
        addressBook: {
            key: 'addressBook',
            routes: ['/receiver/address-book'],
            translationKey: 'addressBook'
        },
        settings: {
            key: 'settings',
            routes: ['/receiver/profile'],
            translationKey: 'settings'
        }
    },
    
    // FREIGHT_FORWARDER
    freight_forwarder: {
        'ask-eve': {
            key: 'ask-eve',
            routes: ['/freight-forwarder/ask-eve'],
            translationKey: 'askEve'
        },
        controlHub: {
            key: 'controlHub',
            routes: ['/freight-forwarder/control-hub'],
            translationKey: 'controlHub'
        },
        quotes: {
            key: 'quotes',
            routes: [
                '/freight-forwarder/quotes',
                '/freight-forwarder/quotes/settings'
            ],
            translationKey: 'quotes'
        },
        containers: {
            key: 'containers',
            routes: ['/freight-forwarder/containers'],
            translationKey: 'containers'
        },
        activity: {
            key: 'activity',
            routes: ['/freight-forwarder/activity'],
            translationKey: 'activity'
        },
        team: {
            key: 'team',
            routes: ['/freight-forwarder/team'],
            translationKey: 'team'
        },
        addressBook: {
            key: 'addressBook',
            routes: ['/freight-forwarder/address-book'],
            translationKey: 'addressBook'
        }
    },
    
    // CARRIER - Basic configuration
    carrier: {
        dashboard: {
            key: 'dashboard',
            routes: ['/carrier/dashboard'],
            translationKey: 'dashboard'
        },
        activity: {
            key: 'activity',
            routes: ['/carrier/activity'],
            translationKey: 'activity'
        },
        team: {
            key: 'team',
            routes: ['/carrier/team'],
            translationKey: 'team'
        }
    },
    
    // PORT_AUTHORITY - Basic configuration
    port_authority: {
        dashboard: {
            key: 'dashboard',
            routes: ['/port-authority/dashboard'],
            translationKey: 'dashboard'
        },
        activity: {
            key: 'activity',
            routes: ['/port-authority/activity'],
            translationKey: 'activity'
        },
        team: {
            key: 'team',
            routes: ['/port-authority/team'],
            translationKey: 'team'
        }
    },
    
    // CUSTOMS_BROKER
    customs_broker: {
        dashboard: {
            key: 'dashboard',
            routes: ['/customs-broker/dashboard'],
            translationKey: 'dashboard'
        },
        documents: {
            key: 'documents',
            routes: ['/customs-broker/documents'],
            translationKey: 'documents'
        },
        activity: {
            key: 'activity',
            routes: ['/customs-broker/activity'],
            translationKey: 'activity'
        },
        team: {
            key: 'team',
            routes: ['/customs-broker/team'],
            translationKey: 'team'
        }
    },
    
    // INSURANCE_PROVIDER - Basic configuration
    insurance_provider: {
        dashboard: {
            key: 'dashboard',
            routes: ['/insurance-provider/dashboard'],
            translationKey: 'dashboard'
        },
        activity: {
            key: 'activity',
            routes: ['/insurance-provider/activity'],
            translationKey: 'activity'
        },
        team: {
            key: 'team',
            routes: ['/insurance-provider/team'],
            translationKey: 'team'
        }
    }
};

/**
 * Helper function per ottenere la configurazione menu items per un company type
 */
export function getMenuItemsForType(companyType: string): Record<string, MenuItemConfig> {
    const normalizedType = companyType.toLowerCase() as CompanyTypeKey;
    return MENU_ITEMS_BY_TYPE[normalizedType] || MENU_ITEMS_BY_TYPE.shipper;
}

/**
 * Helper function per verificare se un path matcha con un route pattern
 */
export function matchesRoute(path: string, routePattern: string): boolean {
    // Exact match
    if (path === routePattern) {
        return true;
    }
    
    // Check if path starts with the pattern (for nested routes)
    // Es: /shipper/containers/123 matcha con /shipper/containers
    if (path.startsWith(routePattern + '/')) {
        return true;
    }
    
    return false;
}

/**
 * Helper function per trovare il menu item key da un path
 */
export function findMenuItemKeyByPath(path: string, companyType: string): string | null {
    const menuItems = getMenuItemsForType(companyType);
    
    for (const [key, config] of Object.entries(menuItems)) {
        for (const route of config.routes) {
            if (matchesRoute(path, route)) {
                return key;
            }
        }
    }
    
    return null;
}

/**
 * Helper function per verificare se un path è accessibile in base ai menu items abilitati
 */
export function isPathAccessible(
    path: string,
    enabledItems: Record<string, boolean>,
    companyType: string
): boolean {
    const menuItemKey = findMenuItemKeyByPath(path, companyType);
    
    if (!menuItemKey) {
        // Path non mappato a nessun menu item, permetti l'accesso di default
        return true;
    }
    
    // Verifica se il menu item è abilitato
    return enabledItems[menuItemKey] === true;
}

